import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { ErrorStateMatcher } from '@angular/material/core';
import { DocumentChangedInfo, FormComponent, FormDocument, FormParameters, FormTemplate, VendorLibrary } from '@formbird/types';
import { ChangedDocumentService, ClientConstants, FormbirdInjectorService } from 'formbird-services';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ComponentSetupService } from '../component-setup.service';

/** Error when invalid control is dirty, touched, or submitted. */
export class MyErrorStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null, form: FormGroupDirective | NgForm | null): boolean {
    const isSubmitted = form && form.submitted;
    return !!(control && control.invalid && (control.dirty || control.touched || isSubmitted));
  }
}

@Component({
  template: '' // template will be overridden in child component
})
export abstract class AbstractComponent<T> implements FormComponent, OnInit, OnDestroy {

  @Input() document: FormDocument;
  @Input() fieldValue: any;
  @Input() template: FormTemplate;
  @Input() fieldName: string;
  @Input() formParameters: FormParameters;
  @Input() key: number;
  @Input() responsiveLayouts: string;
  @Input() message: string;
  @Input() componentDefinition: T;

  vendorLibrariesRel: [VendorLibrary];

  public disabled: boolean;
  public fullWidth: any;
  public elementName: string;
  public formControl: AbstractControl;
  public registeredControlName: string;
  protected validators: any;

  /**
   * A sub-class could avoid calling notifyComponentIntialized on super-class by disabling this flag,
   * they will call that function at the end of their ngOnInit in stead
   * @default true to enable
   */
  protected shouldNotifyCompInitializedInSuper: boolean = true;

  protected shouldDispatchEmptyFieldValue: boolean = true;

  protected subs: Subscription = new Subscription();

  public matcher = new MyErrorStateMatcher();

  protected broadcastService: any;

  protected constructor(
    protected formbirdInjectorService: FormbirdInjectorService,
    protected changedDocumentService: ChangedDocumentService,
    protected componentSetupService: ComponentSetupService,
    protected cdr: ChangeDetectorRef
  ) {
    this.broadcastService = formbirdInjectorService.get('BroadcastService');
  }

  ngOnInit(): void {
    this.setupComponent();
    this.initializeFieldValue();
    this.setupChangeDetection();
    if (this.shouldNotifyCompInitializedInSuper) {
      this.notifyComponentIntialized();
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected setupComponent() {
    this.formControl = this.componentSetupService.setupStandardComponent(this, this.validators);

    this.setFieldActivation(false);
  }

  protected setupChangeDetection(avoidDebounceTime?) {

    if (this.fieldName) {
      const docSub = this.changedDocumentService.watch(this.fieldName, this.document.documentId, this.registeredControlName)
        .subscribe(fieldValue => {
          this.onFieldValueChanged(fieldValue);
        });
      this.subs.add(docSub);
    }

    const compSub = this.changedDocumentService.watchComponentDef(this.fieldName, this.template.documentId).subscribe(compDef => {
      this.onComponentDefChanged(compDef);
    });
    this.subs.add(compSub);

    if (this.formControl) {
      let changeSub;

      if (avoidDebounceTime) {
        changeSub = this.formControl.valueChanges.subscribe(value => {
          this.onControlValueChanged(value);
        });
      } else {
        changeSub = this.formControl.valueChanges.pipe(debounceTime(ClientConstants.VALIDATION_INTERVAL_TIME)).subscribe(value => {
          this.onControlValueChanged(value);
        });
      }

      this.subs.add(changeSub);
    }

    this.setupDocumentCreatedDetection();

  }

  protected setFieldActivation(shouldDetectChanges?) {

    this.componentSetupService.enableField(this.componentDefinition, this.formControl);

    this.detectChanges(shouldDetectChanges);
  }

  /**
   * Call this function to dispatch the field value change action to store
   */
  public valueChanged(value) {

    this.fieldValue = value;

    this.changedDocumentService.valueChanged(this, value, this.registeredControlName);

  }

  /**
   * Call this function to set the field value into store. Param isInitValue is for initializing value and does not trigger undo/redo
   */
  protected dispatchChanged(isInitValue?) {

    const documentId = this.document.documentId;
    const documentChangedInfo: DocumentChangedInfo = {
      documentId: documentId,
      fieldName: this.fieldName,
      newValue: this.fieldValue,
      isInitValue: isInitValue
    };

    this.changedDocumentService.notifyDocumentChanged(documentChangedInfo);

  }

  protected detectChanges(shouldDetectChange?) {

    if (shouldDetectChange !== false && !(this.cdr as ViewRef).destroyed) {
      this.cdr.detectChanges();
    }

  }

  protected updateComponentDefChange(componentDefinition) {

    this.componentDefinition = componentDefinition;
  }

  /**
   * This function will be called once user interacts with the control that manages the field value in the component.
   */
  protected onControlValueChanged(value) {

    this.valueChanged(value);
  }

  /**
   * This function will be called once the template component definition in store gets changed.
   */
  protected onComponentDefChanged(componentDefinition) {

    console.log("Change detection on template: ", componentDefinition.name);

    this.updateComponentDefChange(componentDefinition);

    const disabled = componentDefinition.enabled === false;
    if (this.disabled !== disabled) {

      this.disabled = disabled;

      this.setFieldActivation();
    
    } else {

      this.detectChanges();
    }
  }

  /**
   * This function is used to compare the newValue to the component fieldValue to check if they are the same. 
   * For most components, the field value is primary types (string, number, boolean...) but some complex ones the field value type is object so the component needs to override this function to check correctly.
   * @param newValue 
   * @returns true if newValue equals to the component fieldValue
   */
  protected isEqualFieldValue(newValue) {
    return this.fieldValue === newValue;
  }

  /**
   * This function will be called once the field value in store gets changed.
   */
  protected onFieldValueChanged(fieldValue, shouldDetectChanges = true) {

    if (this.isEqualFieldValue(fieldValue)) {
      return;
    }

    console.log(`On field value changed for component: ${this.registeredControlName} - has label: ${this.componentDefinition['label']}`);

    this.fieldValue = fieldValue;
    if (this.formControl) {
      this.formControl.patchValue(this.fieldValue, {emitEvent: false, onlySelf: true});
    }

    if (shouldDetectChanges) {
      this.detectChanges();
    }
  }

  protected setupDocumentCreatedDetection() {
    const docCreatedSub = this.broadcastService.on(ClientConstants.DOCUMENT_NEW).subscribe((documentInfo) => {
      this.onDocumentCreated(documentInfo);
      this.detectChanges();
    });
    this.subs.add(docCreatedSub);
  }

  /**
   * This function will be called to initialize field value such as: default value... once the New Document button's clicked to create a new document.
   * @param documentInfo includes documentId and unsavedDocumentListId
   */
  protected onDocumentCreated(documentInfo) {

    const document = documentInfo?.document;

    if (document) {

      const unsavedDocumentListId = documentInfo?.unsavedDocumentListId;
      const documentId = document.documentId;

      if (unsavedDocumentListId === this.formParameters.unsavedDocumentListId && this.document.documentId === documentId) {

        // set fieldValue as the origninal field value object
        this.fieldValue = document[this.fieldName];

        // setup fieldValue same as the component initialize in ngOnInit
        this.initializeFieldValue();

        // notify store the component's initialized
        this.notifyComponentIntialized();

      }
    }
  }

  /**
   * This function will initialize the field value including default value if needed
   */
  protected initializeFieldValue() {

  }

  /**
   * notify store the component has been initialized
   */
  protected notifyComponentIntialized() {

    this.changedDocumentService.notifyIntializedField(this, this.shouldDispatchEmptyFieldValue);
  }

  /**
   * Notify the component definition changes to store
   */
  protected notifyComponentDefChanged() {

  }

}
